import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { showSnack } from "react-redux-snackbar";
import { UserState } from "redux-oidc";
import { RouteComponentProps, withRouter } from "react-router";
import { CalendarPickerInput } from "@cadmakers-ui/core";
import { Button, Col, Container, Form, Row, FormGroup, FormText, Label } from "reactstrap";
import { endpoints } from "../..";
import { FeatureRequest, Feedback } from "../../Models/Feedback";
import FileUploadButton from "../../../Common/Components/FileUploadButton/FileUploadButton";
import { fileService, imageUploadService, fileUploadService } from "../../Services";
import ElementSpacer from "../../../Layout/Components/ElementSpacer";
import ImageDropzone from "../Common/ImageDropzone/ImageDropzone";
import { ImageGallery } from "../Common/ImageGallery";
import { FeedbackPriority, FeedbackSize, FeedbackVote } from "../../Constants/feedbackEnums";
import { FeedbackFormInput } from "../Common/FeedbackFormInput";
import FeedbackPrioritySetterContainer from "../Common/FeedbackPrioritySetter/FeedbackPrioritySetterContainer";
import { Image } from "../../Models/Image";
import { ToggleSetting } from "../../../Layout";
import { FeedbackFile } from "../../Models/FeedbackFile";
import FeedbackFileManager from "../Common/FeedbackFileManager/FeedbackFileManager";
import FeedbackFileDisplay from "../Common/FeedbackFileDisplay/FeedbackFileDisplay";
import FeedbackFormTagsInput from "../Common/FeedbackFormTagsInput";
import FeedbackFormIsPrivateInput from "../Common/FeedbackFormIsPrivateInput";
import { IAppState } from "../../../Common";
import { userService } from "../../../User";
import { FeedbackSizeDropdown } from "../Common/FeedbackDropdowns/FeedbackSizeDropdown";

type Props = {
  hidePriorityDropdown: boolean;
  hideCanShowCommentsDropdown: boolean;
  hideTitle: boolean;
  hideImages: boolean;
  hideFiles: boolean;
  readonly: boolean;
  feedback: Feedback;
  canSubmit?: boolean;
  onSubmit?: (feedback: Feedback) => void;
  onDiscard?: () => void;
};

type StateProps = {
  user: UserState;
};

type DispatchProps = {
  showSnack: (uniqueId: any, args: any) => void;
};

type State = {
  featureRequest: FeatureRequest;
  images: Image[];
  files: FeedbackFile[];
  dueDateDatePickerActual: Date;
  startDateDatePickerActual: Date;
};

const mapStateToProps = (state: IAppState) => ({
  user: state.oidc
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args))
});

class EditFeatureRequestForm extends React.Component<
  Props & StateProps & DispatchProps & RouteComponentProps,
  State
> {
  private imageUploadPendingCount: number;

  private fileUploadPendingCount: number;

  static defaultProps = {
    hideTitle: false,
    hideImages: false,
    hideFiles: false,
    readonly: false,
    hidePriorityDropdown: false,
    hideCanShowCommentsDropdown: false
  };

  constructor(props: Props & StateProps & DispatchProps & RouteComponentProps) {
    super(props);

    this.state = {
      featureRequest: this.props.feedback as FeatureRequest,
      images: this.props.feedback.images,
      files: this.props.feedback.files,
      dueDateDatePickerActual: new Date(),
      startDateDatePickerActual: new Date()
    };

    this.imageUploadPendingCount = 0;
    this.fileUploadPendingCount = 0;
    this.onPrioritySelected = this.onPrioritySelected.bind(this);
    this.toggleAreCommentsEnabled = this.toggleAreCommentsEnabled.bind(this);
  }

  clearForm = () => {
    this.setState(prevState => ({
      featureRequest: {
        ...prevState.featureRequest,
        title: "",
        description: "",
        featureRequirements: "",
        useCases: "",
        isPrivate: false,
        estimatedHours: undefined
      }
    }));
  };

  onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;

    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, [name as any]: value }
    }));
  };

  onChangeEstimatedHours = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { name, value } = event.target;
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, [name as any]: parseInt(value, 10) }
    }));
  };

  onChangeTags = (tagIds: number[]) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, tagIds }
    }));
  };

  onChangeIsPrivate = (isPrivate: boolean) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, isPrivate }
    }));
  };

  onChangeStartDate = (startDate: Date) => {
    if (startDate) {
      const dateString = startDate.toISOString();
      this.setState(prevState => ({
        featureRequest: { ...prevState.featureRequest, startDate: dateString }
      }));
    }
  };

  onChangeStartDatepickerActual = (actual: Date) => {
    this.setState(prevState => ({
      startDateDatePickerActual: actual
    }));
  };

  onChangeDueDate = (dueDate: Date) => {
    const dateString = dueDate.toISOString();
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, dueDate: dateString }
    }));
  };

  onChangeDueDatepickerActual = (actual: Date) => {
    this.setState(prevState => ({
      dueDateDatePickerActual: actual
    }));
  };

  onImagesAdded = (images: Image[]) => {
    this.imageUploadPendingCount += images.length;

    images.forEach(async image => {
      await imageUploadService.uploadImage(
        endpoints.feedback.images.uploadTemp(this.props.feedback.code),
        image,
        newImage => {
          this.setState(prevState => ({
            images: prevState.images.concat(newImage)
          }));
        },
        hash => {
          return this.state.images.find(x => x.fullSizeHash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload image", "This image has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar("Failed to upload image", result.error.message, response.status);
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload image",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.imageUploadPendingCount--;
    });
  };

  onImageDelete = (image: Image) => {
    if (image.fullSizeObjectPath) {
      this.setState(prevState => ({
        images: prevState.images.filter(x => x.fullSizeHash !== image.fullSizeHash)
      }));
    }
  };

  onFilesAdded = (files: FeedbackFile[], isCadmakersOnly: boolean) => {
    this.fileUploadPendingCount += files.length;

    files.forEach(async file => {
      await fileUploadService.uploadFile(
        endpoints.feedback.files.uploadTemp(this.props.feedback.code, isCadmakersOnly),
        file,
        newFile => {
          this.setState(prevState => ({
            files: prevState.files.concat(newFile)
          }));
        },
        hash => {
          return this.state.files.find(x => x.hash === hash);
        },
        () => {
          this.showErrorSnackbar("Failed to upload file", "This file has already been uploaded");
        },
        (response, result) => {
          this.showErrorSnackbar(
            "Failed to upload file",
            result?.error?.message ?? "",
            response.status
          );
        },
        response => {
          this.showErrorSnackbar(
            "Failed to upload file",
            "An unknown error has occured",
            response.status
          );
        }
      );

      this.fileUploadPendingCount--;
    });
  };

  onFileDelete = (file: FeedbackFile) => {
    if (file.path) {
      this.setState(prevState => ({
        files: prevState.files.filter(x => x.hash !== file.hash)
      }));
    }
  };

  onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (this.imageUploadPendingCount > 0) {
      this.showErrorSnackbar(
        "Failed to update feature request",
        "Please wait until all images are done uploading"
      );
      return;
    }

    if (this.fileUploadPendingCount > 0) {
      this.showErrorSnackbar(
        "Failed to update feature request",
        "Please wait until all files are done uploading"
      );
      return;
    }

    if (!this.state.featureRequest.application && !this.state.featureRequest.topic) {
      this.showErrorSnackbar("Failed to update feature request", "Please select an app or topic");
      return;
    }

    this.setState(
      prevState => ({
        featureRequest: {
          ...prevState.featureRequest,
          images: prevState.images,
          files: prevState.files
        }
      }),
      () => !!this.props.onSubmit && this.props.onSubmit(this.state.featureRequest)
    );
  };

  onImageUploadClick = (files: File[]) => {
    this.onImagesAdded(fileService.filestoImages(files));
  };

  onFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, false), false);
  };

  onCadmakersFileUploadClick = (files: File[]) => {
    this.onFilesAdded(fileService.filestoFeedbackFiles(files, true), true);
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_BOX", {
      label,
      timeout: 5000
    });
  };

  onPrioritySelected = (feedbackCode: string, priority: FeedbackPriority) => {
    this.setState(prevState => ({
      featureRequest: { ...prevState.featureRequest, priority }
    }));
  };

  getImageGallery = () => {
    if (!this.props.hideImages) {
      if (!this.props.readonly) {
        return (
          <ImageDropzone
            images={this.state.images}
            onImagesAdded={this.onImagesAdded}
            onImageDelete={this.onImageDelete}
          />
        );
      }

      return <ImageGallery images={this.state.images} />;
    }

    return null;
  };

  getFileManager = () => {
    if (!this.props.hideFiles) {
      if (!this.props.readonly) {
        return (
          <FeedbackFileManager
            files={this.state.files.filter(f => !f.isCadmakersOnly)}
            onFilesAdded={this.onFilesAdded}
            onFileDelete={this.onFileDelete}
            isCadmakersOnly={false}
          />
        );
      }

      return <FeedbackFileDisplay files={this.state.files.filter(f => !f.isCadmakersOnly)} />;
    }

    return null;
  };

  getCadmakersFileManager = () => {
    if (!this.props.hideFiles) {
      if (!this.props.readonly) {
        return (
          <FeedbackFileManager
            files={this.state.files.filter(f => f.isCadmakersOnly)}
            onFilesAdded={this.onFilesAdded}
            onFileDelete={this.onFileDelete}
            isCadmakersOnly
          />
        );
      }

      return <FeedbackFileDisplay files={this.state.files.filter(f => f.isCadmakersOnly)} />;
    }

    return null;
  };

  toggleAreCommentsEnabled = () => {
    this.setState(prevState => ({
      featureRequest: {
        ...prevState.featureRequest,
        areCommentsEnabled: !prevState.featureRequest.areCommentsEnabled
      }
    }));
  };

  getSizeoptions = () => {
    const options = [FeedbackSize.Small, FeedbackSize.Medium, FeedbackSize.Large];
    return options;
  };

  onSizeSelected = (selected?: FeedbackSize) => {
    if (selected) {
      this.setState(prevState => ({
        featureRequest: {
          ...prevState.featureRequest,
          feedbackSize: selected
        }
      }));
    }
  };

  render() {
    const isCadmakers = userService.isUserCadmakers(this.props.user);

    return (
      <Container fluid>
        <Form onSubmit={this.onSubmit}>
          {!this.props.hideTitle ? (
            <FeedbackFormInput
              label="Title (Brief Description)"
              type="text"
              name="title"
              autoComplete="off"
              required
              value={this.state.featureRequest.title}
              onChange={e => this.onChange(e)}
              description="This is the displayed title for the feature request. Please make it concise."
              disabled={this.props.readonly}
            />
          ) : null}

          <Row>
            {!this.props.readonly && !this.props.hideCanShowCommentsDropdown && (
              <Col md={2}>
                <ToggleSetting
                  settingName="Comments"
                  state={this.state.featureRequest.areCommentsEnabled}
                  onText="Enabled"
                  offText="Disabled"
                  labeled
                  onToggle={this.toggleAreCommentsEnabled}
                />
                <br />
              </Col>
            )}
            {!this.props.readonly &&
              !!this.props.feedback.permissions &&
              !this.props.hidePriorityDropdown && (
                <Col md={4}>
                  <FeedbackPrioritySetterContainer
                    feedbackCode={this.props.feedback.code}
                    existingPriority={this.props.feedback.priority}
                    includeLabel
                    onChangePriority={this.onPrioritySelected}
                    isReadonly={!this.props.feedback.permissions.canPrioritize}
                  />
                </Col>
              )}
          </Row>
          {isCadmakers && (
            <Row>
              <Col md={6}>
                <FeedbackSizeDropdown
                  value={this.state.featureRequest.feedbackSize}
                  onSelect={this.onSizeSelected}
                  options={this.getSizeoptions()}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col lg={6}>
              <FeedbackFormTagsInput
                selectedTagIds={this.state.featureRequest.tagIds}
                onChangeTags={this.onChangeTags}
              />
            </Col>
            <Col lg={6}>
              <FeedbackFormIsPrivateInput
                isChecked={this.state.featureRequest.isPrivate}
                onChange={this.onChangeIsPrivate}
              />
            </Col>
          </Row>
          <FeedbackFormInput
            label="Description"
            rows={3}
            type="textarea"
            name="description"
            value={
              this.state.featureRequest.description ? this.state.featureRequest.description : ""
            }
            onChange={e => this.onChange(e)}
            description="A description of the feature (e.g. purpose and functionality)"
            disabled={this.props.readonly}
          />
          <FeedbackFormInput
            label="Feature Requirements"
            rows={3}
            type="textarea"
            name="featureRequirements"
            value={
              this.state.featureRequest.featureRequirements
                ? this.state.featureRequest.featureRequirements
                : ""
            }
            onChange={e => this.onChange(e)}
            description="A list of requirements for the requested feature."
            disabled={this.props.readonly}
          />

          <FormGroup>
            <Row>
              {isCadmakers && (
                <Col md={3}>
                  <Label for="startDate">
                    <ElementSpacer spacing=".25rem">Start Date</ElementSpacer>
                  </Label>
                  <div style={{ width: 135 }}>
                    <CalendarPickerInput
                      value={
                        {
                          selectedDate: this.state.featureRequest.startDate
                            ? new Date(this.state.featureRequest.startDate)
                            : undefined,
                          actual: this.state.startDateDatePickerActual
                        } as any
                      }
                      onChange={this.onChangeStartDate}
                      onChangeMonth={this.onChangeStartDatepickerActual}
                      disabledDays={[
                        {
                          before: new Date()
                        }
                      ]}
                    />
                  </div>
                  <FormText>Start date for this task</FormText>
                </Col>
              )}
              <Col md={3}>
                <Label for="dueDate">
                  <ElementSpacer spacing=".25rem">Due Date</ElementSpacer>
                </Label>
                <div style={{ width: 135 }}>
                  <CalendarPickerInput
                    value={
                      {
                        selectedDate: this.state.featureRequest.dueDate
                          ? new Date(this.state.featureRequest.dueDate)
                          : undefined,
                        actual: this.state.dueDateDatePickerActual
                      } as any
                    }
                    onChange={this.onChangeDueDate}
                    onChangeMonth={this.onChangeDueDatepickerActual}
                    disabledDays={[
                      {
                        before: new Date()
                      }
                    ]}
                  />
                </div>
                <FormText>
                  Due date for this feedback to be progressed to the next workflow status
                </FormText>
              </Col>
            </Row>
          </FormGroup>

          {isCadmakers && (
            <FormGroup>
              <Row>
                <Col md={2}>
                  <FeedbackFormInput
                    label="Estimated Hours"
                    type="number"
                    name="estimatedHours"
                    autoComplete="off"
                    value={this.state.featureRequest.estimatedHours}
                    onChange={e => this.onChangeEstimatedHours(e)}
                    description="Estimated number of hours for this task."
                    min={0}
                  />
                </Col>
              </Row>
            </FormGroup>
          )}

          {this.getImageGallery()}

          {this.getFileManager()}
          <br />
          {isCadmakers && this.getCadmakersFileManager()}

          {!this.props.readonly ? (
            <>
              <hr />
              <Row>
                <Col xs="auto">
                  <ElementSpacer>
                    <Button disabled={!this.props.canSubmit}>Save</Button>
                    <Button onClick={this.props.onDiscard}>Discard</Button>
                    <Button onClick={this.clearForm}>Clear</Button>
                    <FileUploadButton onClick={this.onImageUploadClick}>
                      Upload Images
                    </FileUploadButton>
                    <FileUploadButton onClick={this.onFileUploadClick}>
                      Upload Files
                    </FileUploadButton>
                    {isCadmakers && (
                      <FileUploadButton onClick={this.onCadmakersFileUploadClick}>
                        Upload Cadmakers Internal Files
                      </FileUploadButton>
                    )}
                  </ElementSpacer>
                </Col>
              </Row>
            </>
          ) : null}
        </Form>
      </Container>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditFeatureRequestForm));
